import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Compass, GraduationCap, Briefcase } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Countdown from "@/components/Countdown";
import { BrandMark, GeoBackdrop, AmbientGlow } from "@/components/Brand";

const PILLARS = [
    {
        index: "01",
        title: "For Brands",
        tagline: "Let's build what matters.",
        body:
            "For ambitious brands, founders and creators looking for meaningful growth. A long-form partnership built around your unfair advantage.",
        cta: "Start Your Application",
        to: "/clients",
        icon: Compass,
        testid: "pillar-clients",
    },
    {
        index: "02",
        title: "Dr. Aangan Theory",
        tagline: "We diagnose. You grow.",
        body:
            "A premium Brand Diagnosis service. Brand Scan, Growth Assessment and a Growth Prescription you can act on by Monday.",
        cta: "Book a Diagnosis",
        to: "/doctor",
        icon: Sparkles,
        testid: "pillar-doctor",
    },
    {
        index: "03",
        title: "Aangan Academy",
        tagline: "Learn. Build. Lead.",
        body:
            "Selective training programs for future marketers, creators and strategists. Built around live engagements and mentorship.",
        cta: "Apply for a Program",
        to: "/academy",
        icon: GraduationCap,
        testid: "pillar-academy",
    },
    {
        index: "04",
        title: "Careers",
        tagline: "Build your career. Build with us.",
        body:
            "Internships and full-time roles for people who want their work to matter. Bring craft, curiosity and conviction.",
        cta: "Explore Openings",
        to: "/careers",
        icon: Briefcase,
        testid: "pillar-careers",
    },
];

function Pillar({ p }) {
    const Icon = p.icon;
    return (
        <Link
            to={p.to}
            data-testid={p.testid}
            className="card-surface p-8 lg:p-10 group flex flex-col justify-between min-h-[360px] relative overflow-hidden"
        >
            <div className="flex items-start justify-between">
                <span className="font-mono text-xs tracking-[0.2em] text-saffron">{p.index}</span>
                <Icon size={18} strokeWidth={1.5} className="text-white/40 group-hover:text-saffron transition-colors" />
            </div>
            <div className="mt-12">
                <h3 className="font-display text-2xl lg:text-3xl tracking-tight mb-3">{p.title}</h3>
                <div className="text-saffron text-sm mb-5">{p.tagline}</div>
                <p className="text-white/55 text-sm leading-relaxed mb-8 max-w-md">{p.body}</p>
                <div className="inline-flex items-center gap-2 text-sm text-white/85 group-hover:text-saffron transition-colors">
                    {p.cta}
                    <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
            </div>
            {/* faint corner geometry */}
            <svg className="absolute -right-10 -bottom-10 opacity-[0.04] pointer-events-none" width="240" height="240" viewBox="0 0 100 100">
                <path d="M50 8 L88 88 L72 88 L50 30 L28 88 L12 88 Z" fill="#fff" />
                <path d="M62 78 L92 78 L88 92 L58 92 Z" fill="#FF9933" />
            </svg>
        </Link>
    );
}

export default function Home() {
    return (
        <main className="min-h-screen relative" data-testid="home-page">
            <Navbar />

            {/* HERO */}
            <section className="relative pt-40 pb-32 lg:pt-56 lg:pb-40 overflow-hidden">
                <GeoBackdrop />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-8 fade-up">
                            <div className="eyebrow mb-6 flex items-center gap-3">
                                <span className="inline-block w-2 h-2 bg-saffron" />
                                Made to Matter
                            </div>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] tracking-tighter leading-[0.95] mb-10">
                                We don't do <span className="text-white/35">marketing</span>.
                                <br />
                                We unlock <span className="text-saffron">growth</span>.
                            </h1>
                            <p className="text-white/65 text-base lg:text-lg leading-relaxed max-w-xl mb-10">
                                Strategic thinking. Creative excellence. Meaningful impact. Aangan Theory is a
                                growth partner for brands, founders and creators who refuse to be ignored.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Link to="/clients" className="btn-primary" data-testid="hero-cta-primary">
                                    Begin Your Growth Journey
                                    <ArrowUpRight size={16} />
                                </Link>
                                <Link to="/doctor" className="btn-secondary" data-testid="hero-cta-secondary">
                                    Book a Brand Diagnosis
                                    <ArrowUpRight size={16} />
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-4 fade-up delay-2">
                            <div className="border-l border-white/10 pl-6 ml-2 space-y-6 max-w-sm">
                                {[
                                    "Strategic thinking",
                                    "Creative excellence",
                                    "Meaningful impact",
                                ].map((line, i) => (
                                    <div key={i} className="flex gap-4 items-start">
                                        <span className="font-mono text-xs text-saffron mt-1.5">0{i + 1}</span>
                                        <span className="text-white/85 text-base">{line}.</span>
                                    </div>
                                ))}
                                <div className="hairline" />
                                <div className="eyebrow">
                                    A growth partnership — <span className="text-white/85">not an agency.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="py-24 lg:py-32 border-t border-white/8 relative overflow-hidden">
                <AmbientGlow side="left" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
                        <div className="max-w-2xl">
                            <div className="eyebrow mb-5">Four pillars · one philosophy</div>
                            <h2 className="font-display text-4xl lg:text-5xl tracking-tighter leading-tight">
                                Where growth gets built.
                            </h2>
                        </div>
                        <p className="text-white/55 text-sm max-w-md">
                            Pick your way in. Each pillar is independent — but together they form an ecosystem
                            built to make people, brands and businesses impossible to ignore.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
                        {PILLARS.map((p) => (
                            <Pillar key={p.title} p={p} />
                        ))}
                    </div>
                </div>
            </section>

            {/* MANIFESTO */}
            <section className="py-24 lg:py-32 border-t border-white/8 relative overflow-hidden bg-warm-noise">
                <AmbientGlow side="right" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <div className="eyebrow mb-4">Manifesto</div>
                            <div className="flex items-center gap-3">
                                <BrandMark size={42} />
                                <div className="font-display text-2xl">Made to Matter</div>
                            </div>
                        </div>
                        <div className="lg:col-span-8">
                            <p className="font-display text-3xl lg:text-4xl xl:text-5xl tracking-tighter leading-[1.1] text-white">
                                We help people, brands and businesses become{" "}
                                <span className="text-saffron">impossible to ignore</span>. Through strategy
                                that thinks, work that lasts, and partnerships built like institutions —
                                not invoices.
                            </p>
                            <div className="hairline mt-12 mb-8" />
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                                {[
                                    ["08+", "Disciplines"],
                                    ["100%", "Independently owned"],
                                    ["04 Jul", "Public launch"],
                                    ["1", "Standard. Yours."],
                                ].map(([k, v]) => (
                                    <div key={k}>
                                        <div className="font-mono text-2xl lg:text-3xl text-white">{k}</div>
                                        <div className="eyebrow mt-2">{v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* COUNTDOWN */}
            <section className="py-24 lg:py-32 border-t border-white/8">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-4">Public launch</div>
                    <h2 className="font-display text-4xl lg:text-5xl tracking-tighter mb-12">
                        We open the doors on <span className="text-saffron">4th July</span>.
                    </h2>
                    <Countdown />
                    <p className="text-white/45 text-sm mt-8 max-w-xl">
                        Until then, applications for clients, the Academy and Dr. Aangan Theory are open
                        by invitation. Apply early — slots are limited by design.
                    </p>
                </div>
            </section>

            <Footer />
        </main>
    );
}
